'use client'

import * as React from 'react'
import {Blocks} from '@builder.io/sdk-react'
import {Tabs, TabsList, TabsTrigger, TabsContent} from '@/components/ui/tabs'
import {cn} from '@/lib/utils'

export type BuilderBlock = any

export type ContentTabsItem = {
    triggerBlocks?: BuilderBlock[]
    panelBlocks?: BuilderBlock[]
}

export type ContentTabsProps = React.HTMLAttributes<HTMLDivElement> & {
    items?: ContentTabsItem[]
    defaultTab?: number
    builderBlock?: {
        id?: string
    }
    'data-testid'?: string
}

export const ContentTabs = React.forwardRef<HTMLDivElement, ContentTabsProps>(
    (
        {items = [], defaultTab = 0, builderBlock, className, 'data-testid': dataTestId, ...props},
        ref
    ) => {
        const safeItems = items.length > 0 ? items : [{triggerBlocks: [], panelBlocks: []}]

        const safeDefaultTab = defaultTab || 1
        const clampedTab = Math.min(Math.max(safeDefaultTab, 1), safeItems.length)
        const defaultValue = `tab-${clampedTab}`

        return (
            <div
                ref={ref}
                className={cn('w-full', className)}
                data-testid={dataTestId || 'contentTabs-ContentTabs'}
                {...props}
            >
                <Tabs defaultValue={defaultValue}>
                    <TabsList
                        className="h-auto flex-wrap justify-start"
                        data-testid="contentTabs-TabsList"
                    >
                        {safeItems.map((item, index) => {
                            const value = `tab-${index + 1}`

                            return (
                                <TabsTrigger
                                    key={value}
                                    value={value}
                                    className="h-auto !whitespace-normal"
                                    data-testid="contentTabs-TabsTrigger"
                                >
                                    <Blocks
                                        path={`component.options.items.${index}.triggerBlocks`}
                                        blocks={item.triggerBlocks || []}
                                        parent={builderBlock?.id}
                                        data-testid="contentTabs-TriggerBlocks"
                                    />
                                </TabsTrigger>
                            )
                        })}
                    </TabsList>

                    {safeItems.map((item, index) => {
                        const value = `tab-${index + 1}`

                        return (
                            <TabsContent
                                key={value}
                                value={value}
                                data-testid="contentTabs-TabsContent"
                            >
                                <Blocks
                                    path={`component.options.items.${index}.panelBlocks`}
                                    blocks={item.panelBlocks || []}
                                    parent={builderBlock?.id}
                                    data-testid="contentTabs-PanelBlocks"
                                />
                            </TabsContent>
                        )
                    })}
                </Tabs>
            </div>
        )
    }
)

ContentTabs.displayName = 'ContentTabs'
